import { useCallback, useEffect, useState } from 'react';
import SEO from '../components/SEO';
import SafeMarkdown from '../components/SafeMarkdown';
import { useAuth } from '../context/AuthContext';
import { visibilityOptions } from '../data/widgets';
import { ensureDefaultPrivacyGroups, listPrivacyGroups } from '../services/categoryService';
import { createStory, deleteStory, listOwnerStories, updateStory } from '../services/contentService';

const emptyStory = {
  id: '',
  title: '',
  summary: '',
  content: '',
  visibility: 'private',
  visibilityGroupId: '',
};

function Stories() {
  const { currentUser } = useAuth();
  const [stories, setStories] = useState([]);
  const [privacyGroups, setPrivacyGroups] = useState([]);
  const [form, setForm] = useState(emptyStory);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadStories = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      await ensureDefaultPrivacyGroups(currentUser.uid);
      const [loadedStories, loadedGroups] = await Promise.all([
        listOwnerStories(currentUser.uid),
        listPrivacyGroups(currentUser.uid),
      ]);
      setStories(loadedStories);
      setPrivacyGroups(loadedGroups);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadStories();
  }, [loadStories]);

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function editStory(story) {
    setForm({
      id: story.id,
      title: story.title || '',
      summary: story.summary || '',
      content: story.content || '',
      visibility: story.visibility || 'private',
      visibilityGroupId: story.visibilityGroupId || '',
    });
    setMessage('Editing story draft.');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (!form.title.trim()) {
        throw new Error('Story title is required.');
      }
      if (form.id) {
        await updateStory(form.id, form);
        setMessage('Story updated.');
      } else {
        await createStory(currentUser.uid, form);
        setMessage('Story created.');
      }
      setForm(emptyStory);
      await loadStories();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(storyId) {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await deleteStory(storyId);
      setMessage('Story deleted.');
      if (form.id === storyId) {
        setForm(emptyStory);
      }
      await loadStories();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <SEO title="Stories" />
      <section className="page-heading">
        <p className="eyebrow">Life chapters</p>
        <h1>Write the stories behind the profile.</h1>
        <p>Stories are saved in Firestore and only public stories appear on the public profile.</p>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <section className="editor-layout">
        <form className="surface-card stacked-form" onSubmit={handleSubmit}>
          <h2>{form.id ? 'Edit story' : 'New story'}</h2>
          <label>Title<input value={form.title} onChange={(event) => updateForm('title', event.target.value)} /></label>
          <label>Summary<input value={form.summary} onChange={(event) => updateForm('summary', event.target.value)} /></label>
          <label>Story <span className="field-hint">Markdown supported</span><textarea rows="9" value={form.content} onChange={(event) => updateForm('content', event.target.value)} /></label>
          <label>Visibility
            <select value={form.visibility} onChange={(event) => updateForm('visibility', event.target.value)}>
              {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
            </select>
          </label>
          {form.visibility === 'group' ? (
            <label>Group
              <select value={form.visibilityGroupId || ''} onChange={(event) => updateForm('visibilityGroupId', event.target.value)} required>
                <option value="">Select group</option>
                {privacyGroups.map((group) => <option value={group.id} key={group.id}>{group.name}</option>)}
              </select>
            </label>
          ) : null}
          <div className="button-row compact-row">
            <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save story'}</button>
            {form.id && <button className="button button-secondary" type="button" onClick={() => setForm(emptyStory)}>Cancel edit</button>}
          </div>
        </form>
        <div className="surface-card">
          <h2>Your stories</h2>
          {loading ? <p>Loading stories...</p> : null}
          {!loading && stories.length === 0 ? <p className="muted">No stories yet. Start with one small scene.</p> : null}
          <div className="item-list">
            {stories.map((story) => (
              <article className="content-item" key={story.id}>
                <p className="tag">{story.visibility}</p>
                <h3>{story.title}</h3>
                <p>{story.summary || story.content || 'No text yet.'}</p>
                <div className="button-row compact-row">
                  <button className="button button-secondary" type="button" onClick={() => editStory(story)}>Edit</button>
                  <button className="button button-secondary" type="button" onClick={() => handleDelete(story.id)} disabled={saving}>Delete</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      {form.content.trim() && (
        <section className="surface-card preview-panel">
          <p className="eyebrow">Markdown preview</p>
          <h2>{form.title || 'Untitled story'}</h2>
          <SafeMarkdown>{form.content}</SafeMarkdown>
        </section>
      )}
    </>
  );
}

export default Stories;
